import React, { lazy, Suspense, useEffect } from "react";
import * as amplitude from "@amplitude/analytics-browser";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import GlobalSnack from "./components/GlobalSnack";
import Auth from "./layouts/Auth";
import Dashboard from "./layouts/Dashboard";
import ModuleLayout from "./layouts/ModuleLayout";
import Theme from "./theme";
import { protectedRoutes, publicRoutes } from "./routes";
import useAuthStore from "./store/useAuthStore";
import CustomLoader from "./pages/private/Module/UI/CustomLoader";
import PrivateRouteWrapper from "./layouts/PrivateRouteWrapper";

const NoMatch = lazy(() => import("@/pages/public/NoMatch"));
const Module = lazy(() => import("@/pages/private/Module"));

if (process.env.NODE_ENV === "production") {
  amplitude.init(process.env.REACT_APP_AMPLITUDE_KEY, {});
}

function App() {
  const location = useLocation();
  const initAuthStore = useAuthStore((state) => state.initAuthStore);
  const isAuthenticating = useAuthStore((state) => state.isAuthenticating);

  useEffect(() => {
    initAuthStore(location);
  }, []);

  return (
    <div className="App">
      <StyledThemeProvider theme={Theme}>
        <ThemeProvider theme={Theme}>
          <CssBaseline />
          {isAuthenticating ? (
            <div
              style={{
                background: "#F7F9FC",

                height: "100%",
                width: "100%",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CustomLoader text="Authenticating User" />
            </div>
          ) : (
            <Routes>
              <Route element={<Auth />}>
                {publicRoutes.map((route) => (
                  <Route
                    key={route?.name}
                    path={route?.path}
                    element={
                      route?.shouldSuspend ? (
                        <Suspense>
                          <route.component name={route.name} />
                        </Suspense>
                      ) : (
                        <>
                          <route.component name={route.name} />
                        </>
                      )
                    }
                  />
                ))}
              </Route>
              <Route
                element={
                  <PrivateRouteWrapper>
                    <ModuleLayout />
                  </PrivateRouteWrapper>
                }
              >
                <Route
                  path="module/:id/"
                  element={
                    <Suspense>
                      <Module />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                element={
                  <PrivateRouteWrapper>
                    <Dashboard />
                  </PrivateRouteWrapper>
                }
              >
                {protectedRoutes.map((route) => (
                  <Route
                    key={route.name}
                    path={route.path}
                    element={
                      route.shouldSuspend ? (
                        <Suspense>
                          <route.component name={route.name} />
                        </Suspense>
                      ) : (
                        <>
                          <route.component name={route.name} />
                        </>
                      )
                    }
                  />
                ))}
              </Route>
              <Route
                path="*"
                element={
                  <Suspense>
                    <NoMatch />
                  </Suspense>
                }
              />
            </Routes>
          )}
          <GlobalSnack />
        </ThemeProvider>
      </StyledThemeProvider>
    </div>
  );
}

export default App;
